/**
 * Settings > Case Preparation: the firm defaults the case workflows
 * (Organize, Number, Retitle, Research, Letters, Forms, Packet) run with.
 * Everyone at the firm sees them; owners and admins change them.
 *
 * The firm record is fetched once by Settings and passed in; nothing here
 * renders a control until it has loaded, so no save can spread a record it
 * never read. A workflow's "For This Case Only" note changes one case without
 * changing these.
 *
 * The sections, in order: Exhibits, Folder Structure, Drafting, Custom Case
 * Types (where Custom Workflow is on), Filing Packet, Case Insights and Firm
 * Instructions, each a header over its own card, with jump
 * links above them. Firm Instructions is the firm's own document
 * (`agents-md`) with its own load, and only its editor writes it: the other
 * sections save on the firm record.
 */
import { Lock } from "lucide-react";
import { useEffect, useRef, type FC, type ReactNode } from "react";

import type { LawFirm } from "@/api/types/lawFirm";
import {
  ExhibitNumberingGroup,
  ExhibitTitlesGroup
} from "@/components/settings/FirmHouseStyleSection";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { customWorkflowEnabled } from "@/features/customCaseTypes/customCaseTypes";

import {
  CASE_INSIGHTS_ANCHOR,
  CaseInsightsSection
} from "./CaseInsightsSection";
import { CasePrepSection } from "./casePrepParts";
import {
  CUSTOM_CASE_TYPES_ANCHOR,
  CustomCaseTypesSection
} from "./CustomCaseTypesSection";
import { DraftingTemplatesSection } from "./DraftingTemplatesSection";
import { FirmInstructionsSection } from "./FirmInstructionsSection";
import { FolderStructureSection } from "./FolderStructureSection";
import { PacketStyleSection } from "./PacketStyleSection";
import type { SettingsFirm } from "./useSettingsFirm";

export const READ_ONLY_COPY = "Only firm admins can change these.";

/** The Firm Instructions anchor, for `initialSection`. */
export const FIRM_INSTRUCTIONS_ANCHOR = "firm-instructions";
export const FIRM_INSTRUCTIONS_SECTION = `case-preparation#${FIRM_INSTRUCTIONS_ANCHOR}`;

type Props = {
  lawFirmId: string | null;
  firmState: SettingsFirm;
  /** Owner / admin (or platform superadmin); members see the values read-only. */
  canEdit: boolean;
  /** Close Settings before following a link into the app. */
  onNavigate?: () => void;
  /** Scroll to this section on open, by anchor (`FIRM_INSTRUCTIONS_ANCHOR`). */
  focus?: string | null;
};

type Section = { id: string; label: string; body: ReactNode };

const sectionsOf = ({
  lawFirmId,
  firm,
  canEdit,
  onSaved,
  onNavigate,
  jump
}: {
  lawFirmId: string;
  firm: LawFirm;
  canEdit: boolean;
  onSaved: (firm: LawFirm) => void;
  onNavigate?: () => void;
  jump: (id: string) => void;
}): Section[] => {
  const saves = { lawFirmId, firm, canEdit, onSaved };
  return [
    {
      id: "case-prep-exhibits",
      label: "Exhibits",
      body: (
        <CasePrepSection
          title="Exhibits"
          description="How Retitle Exhibits words each title, the labels new cases start with and how letters cite exhibits."
          testId="case-prep-exhibits"
        >
          <ExhibitTitlesGroup {...saves} />
          <ExhibitNumberingGroup {...saves} />
        </CasePrepSection>
      )
    },
    {
      id: "case-prep-folders",
      label: "Folder Structure",
      body: <FolderStructureSection {...saves} onNavigate={onNavigate} />
    },
    {
      id: "case-prep-drafting",
      label: "Drafting",
      body: (
        <DraftingTemplatesSection
          lawFirmId={lawFirmId}
          canEdit={canEdit}
          onNavigate={onNavigate}
        />
      )
    },
    ...(customWorkflowEnabled()
      ? [
          {
            id: CUSTOM_CASE_TYPES_ANCHOR,
            label: "Custom Case Types",
            body: (
              <CustomCaseTypesSection
                lawFirmId={lawFirmId}
                onJumpToDrafting={() => jump("case-prep-drafting")}
              />
            )
          }
        ]
      : []),
    {
      id: "case-prep-packet",
      label: "Filing Packet",
      body: <PacketStyleSection {...saves} />
    },
    {
      id: CASE_INSIGHTS_ANCHOR,
      label: "Case Insights",
      body: <CaseInsightsSection {...saves} />
    },
    {
      id: FIRM_INSTRUCTIONS_ANCHOR,
      label: "Firm Instructions",
      body: (
        <FirmInstructionsSection
          lawFirmId={lawFirmId}
          canEdit={canEdit}
          firmName={firm.name}
        />
      )
    }
  ];
};

const LoadingState = () => (
  <div
    className="flex flex-col gap-8 px-6 pt-6"
    data-testid="case-prep-loading"
  >
    {[0, 1].map((i) => (
      <div key={i} className="flex flex-col gap-3">
        <Skeleton className="h-4 w-40 bg-fill-2" />
        <Skeleton className="h-40 w-full rounded-xl bg-fill-2" />
      </div>
    ))}
  </div>
);

export const CasePreparationTab: FC<Props> = ({
  lawFirmId,
  firmState,
  canEdit,
  onNavigate,
  focus = null
}) => {
  const { firm, loading, error, retry, replace } = firmState;
  const anchors = useRef(new Map<string, HTMLElement>());
  // The sections render once the firm has loaded.
  const landedOn = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = focus ? anchors.current.get(focus) : undefined;
    if (!el || landedOn.current === el) return;
    el.scrollIntoView?.({ block: "start" });
    landedOn.current = el;
  }, [focus, firm, loading]);

  const anchor = (id: string) => (el: HTMLElement | null) => {
    if (el) anchors.current.set(id, el);
    else anchors.current.delete(id);
  };
  const jump = (id: string) =>
    anchors.current.get(id)?.scrollIntoView?.({ block: "start" });

  const sections =
    lawFirmId && firm && !loading && !error
      ? sectionsOf({
          lawFirmId,
          firm,
          canEdit,
          onSaved: replace,
          onNavigate,
          jump
        })
      : null;

  return (
    <div
      className="flex w-full flex-col pb-8"
      data-testid="case-preparation-tab"
    >
      <div className="flex flex-col gap-2 px-6 pt-1">
        <p className="text-sm text-fg-3">
          The defaults Casey&apos;s case workflows use. A workflow&apos;s
          &quot;For This Case Only&quot; note changes one case without changing
          these.
        </p>
        {!canEdit ? (
          <p
            className="flex items-center gap-1.5 text-sm text-fg-2"
            data-testid="case-prep-read-only"
          >
            <Lock className="size-3.5 shrink-0 text-fg-3" aria-hidden />
            {READ_ONLY_COPY}
          </p>
        ) : null}
      </div>
      {!lawFirmId ? (
        <p className="px-6 pt-6 text-sm text-fg-3">
          Join a firm to see its Case Preparation defaults.
        </p>
      ) : error ? (
        <div
          className="flex items-center justify-between gap-4 px-6 pt-6"
          data-testid="case-prep-error"
        >
          <span className="text-sm text-danger">
            Couldn&apos;t load the firm&apos;s settings. {error}
          </span>
          <Button variant="secondary" size="sm" onClick={retry}>
            Retry
          </Button>
        </div>
      ) : !sections ? (
        <LoadingState />
      ) : (
        <>
          <nav
            aria-label="Case Preparation Sections"
            className="sticky top-0 z-10 mt-4 border-b border-line-1 bg-surface px-6 py-2"
            data-testid="case-prep-nav"
          >
            <div className="-mx-1.5 flex flex-wrap gap-1">
              {sections.map((s) => (
                <Button
                  key={s.id}
                  variant="ghost"
                  size="xs"
                  onClick={() => jump(s.id)}
                >
                  {s.label}
                </Button>
              ))}
            </div>
          </nav>
          <div className="flex flex-col gap-8 px-6 pt-6">
            {sections.map((s) => (
              <div
                key={s.id}
                id={s.id}
                ref={anchor(s.id)}
                className="scroll-mt-14"
              >
                {s.body}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
